import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, Plus, Search, X } from "lucide-react";
import EntityLink from "../components/EntityLink";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useScopedModuleId } from "../contexts/ModuleScopeContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import EmployeePicker from "../components/EmployeePicker";
import { Textarea } from "../components/ui/textarea";
import { TableSkeleton } from "../components/SkeletonHelpers";
import EmptyState from "../components/EmptyState";
import EvidenceUpload from "../components/EvidenceUpload";
import AttachmentsField from "../components/AttachmentsField";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { usePageAction } from "../contexts/UXChromeContext";
import { toast } from "sonner";
import RowActions from "../components/RowActions";
import ShowDeletedToggle, { DeletedRowDecorator, DeletedPill } from "../components/ShowDeletedToggle";
import { Checkbox } from "../components/ui/checkbox";
import useBulkSelection from "../hooks/useBulkSelection";
import BulkActionsBar from "../components/BulkActionsBar";
import { useHasPermission } from "../contexts/PermissionsContext";
import { rowOpenProps } from "../hooks/useRowOpen";
import useFilterUrlSync from "../hooks/useFilterUrlSync";
import DetailDialog from "../components/DetailDialog";
import DateInput from "../components/DateInput";
import ModuleIdChip from "../components/ModuleIdChip";
import LeaveBalanceLookup from "../components/LeaveBalanceLookup";
import { EntitledCell, UsedCell } from "../components/LeaveBalanceCell";

// Iter60.p53 — Status pill tones. `pending_hr` shifted from pure blue
// to a navy tint for brand parity with the rest of ASL (Attendance /
// Payroll use navy for admin-workflow states). Rejected stays crimson
// since it is a terminal negative outcome.
const STATUS_TONE = {
    pending_dept: "bg-amber-50 text-amber-700 border border-amber-200",
    pending_hr: "bg-[color:var(--asl-navy-50)] text-[color:var(--asl-navy)] border border-[color:var(--asl-navy-50)]",
    approved: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    rejected: "bg-rose-50 text-[color:var(--asl-crimson)] border border-rose-200",
    cancelled: "bg-gray-100 text-gray-600 border border-gray-200",
};

export default function Leave() {
    const navigate = useNavigate();
    const { moduleCode: pathModuleCode } = useParams();
    const { user } = useAuth();
    const scopedId = useScopedModuleId();
    const canCreate = useHasPermission("leave.create_request", scopedId || null);
    const canDeptApprove = useHasPermission("leave.dept_approve", scopedId || null);
    const canHRApprove = useHasPermission("leave.hr_approve", scopedId || null);

    const [items, setItems] = useState([]);
    const [detail, setDetail] = useState(null);
    const [types, setTypes] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState({ status: "all" });
    const [showDeleted, setShowDeleted] = useState(false);
    // Iter60.p47 Item D — search chip on Requests tab (debounced 300ms).
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    // Iter70 — Two-way URL sync for the Leave Requests filter bar.
    useFilterUrlSync({
        filters: { status: filter.status, search, show_deleted: showDeleted },
        defaults: { status: "all", search: "", show_deleted: false },
        paramMap: {
            status: "status",
            search: "search",
            show_deleted: "show_deleted",
        },
        hydrate: (patch) => {
            if (patch.status !== undefined) setFilter((f) => ({ ...f, status: patch.status }));
            if (patch.search !== undefined) setSearch(patch.search);
            if (patch.show_deleted !== undefined) setShowDeleted(!!patch.show_deleted);
        },
    });

    const clearAllFilters = React.useCallback(() => {
        setFilter({ status: "all" });
        setSearch("");
        setShowDeleted(false);
    }, []);

    // Iter56 → Iter56c — bulk selection (HR-level bulk approve + soft-delete).
    // Module-scoped to match individual row buttons.
    const canBulkDelete = useHasPermission("leave.delete", scopedId || null);
    const canBulkApprove = canHRApprove || canDeptApprove;
    const bulk = useBulkSelection(items, (r) => r.id);

    usePageAction(canCreate ? { label: "New Leave Request", run: () => setOpen(true) } : null, [canCreate]);

    const load = async () => {
        setLoading(true);
        try {
            const params = { page_size: 200 };
            if (scopedId) params.module_id = scopedId;
            if (filter.status !== "all") params.status = filter.status;
            if (showDeleted) params.include_deleted = true;
            if (debouncedSearch) params.search = debouncedSearch;
            const [r, t, e] = await Promise.all([
                api.get("/leave/requests", { params }),
                api.get("/leave/types"),
                api.get("/employees", { params: { module_id: scopedId, page_size: 200 } }),
            ]);
            setItems(r.data.items || []);
            setTypes(t.data || []);
            setEmployees((e.data.items || e.data || []).filter(x => x.deleted_at === null || !x.deleted_at));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [scopedId, filter.status, debouncedSearch, showDeleted]);

    const typeMap = useMemo(() => Object.fromEntries(types.map(t => [t.id, t])), [types]);
    const empMap = useMemo(() => Object.fromEntries(employees.map(e => [e.id, e])), [employees]);

    // Iter73 — Overrun-acknowledgment dialog state. When approve
    // returns `overrun_acknowledgment_required`, we surface the
    // warning + checkbox and re-post with `acknowledged_overrun: true`.
    const [overrunDialog, setOverrunDialog] = useState(null);
    const [overrunAck, setOverrunAck] = useState(false);

    const action = async (id, path, opts = {}) => {
        try {
            await api.post(`/leave/requests/${id}/${path}`, {
                note: "",
                acknowledged_overrun: !!opts.acknowledged_overrun,
            });
            toast.success("Done");
            await load();
        } catch (err) {
            // Iter73 — Detect the balance-overrun 400 and open the ack dialog.
            const detail = err?.response?.data?.detail;
            if (
                detail
                && typeof detail === "object"
                && detail.error === "overrun_acknowledgment_required"
            ) {
                setOverrunAck(false);
                setOverrunDialog({
                    id, path,
                    overrun_days: detail.overrun_days,
                    requested_days: detail.requested_days,
                    available_before_approval: detail.available_before_approval,
                    employee_name: empMap[items.find(r => r.id === id)?.employee_id]?.full_name || "",
                    leave_type_name: typeMap[items.find(r => r.id === id)?.leave_type_id]?.name || "",
                });
                return;
            }
            toast.error(formatApiError(err));
        }
    };

    return (
        <section className="space-y-4" data-testid="leave-page">
            <Tabs defaultValue="requests">
                <TabsList data-testid="leave-tabs">
                    <TabsTrigger value="requests" data-testid="leave-tab-requests">Requests</TabsTrigger>
                    <TabsTrigger value="calendar" data-testid="leave-tab-calendar">Calendar</TabsTrigger>
                    <TabsTrigger value="balances" data-testid="leave-tab-balances">Balances</TabsTrigger>
                </TabsList>
                <TabsContent value="requests" className="space-y-4 pt-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 flex-wrap">
                    <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
                        <SelectTrigger className="h-9 w-44" data-testid="leave-status-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="pending_dept">Pending Dept</SelectItem>
                            <SelectItem value="pending_hr">Pending HR</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                    </Select>
                    {/* Iter60.p47 Item D — search chip. Matches name / EMP code /
                        Module ID / biometric ID (case-insensitive substring). */}
                    <div className="relative">
                        <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--asl-muted)]" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name or Module ID…"
                            className="h-9 pl-8 pr-8 w-72 rounded border text-sm bg-transparent"
                            style={{ borderColor: "var(--asl-border)" }}
                            data-testid="leave-search-input"
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={() => setSearch("")}
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100"
                                aria-label="Clear search"
                                data-testid="leave-search-clear"
                            >
                                <X className="h-3.5 w-3.5 text-[color:var(--asl-muted)]" />
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Iter70 — Reset every managed filter (status, search,
                        show-deleted) to its default and clear the URL. */}
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={clearAllFilters}
                        data-testid="leave-clear-filters"
                        title="Reset all filters to their defaults"
                    >
                        Clear filters
                    </Button>
                    <ShowDeletedToggle value={showDeleted} onChange={setShowDeleted} testid="leave-show-deleted" />
                    {canCreate && (
                        <Button onClick={() => setOpen(true)} className="asl-navy-bg text-white" data-testid="leave-new-btn">
                            <Plus className="h-4 w-4 mr-1" /> New Leave Request
                        </Button>
                    )}
                </div>
            </div>

            <div className="asl-card overflow-hidden">
                {loading ? <TableSkeleton rows={5} cols={8} testid="leave-loading" />
                : items.length === 0 ? (
                    <EmptyState
                        icon={Calendar}
                        title={filter.status !== "all" ? "Nothing matches these filters. Try widening the range or clearing a filter." : "No leave requests yet"}
                        description={filter.status !== "all" ? "Adjust filters or clear them." : "Submit a leave request to get started."}
                        ctaLabel={canCreate ? "New Leave Request" : undefined}
                        onCta={canCreate ? () => setOpen(true) : undefined}
                        filtered={filter.status !== "all"}
                        onClear={() => setFilter({ status: "all" })}
                        testid="leave-empty"
                    />
                ) : (
                    <>
                    {(canBulkApprove || canBulkDelete) && (
                        <BulkActionsBar
                            resource="leave/requests"
                            selectedIds={bulk.selectedIds}
                            onClear={bulk.clear}
                            onRefresh={load}
                            entityLabel="leave request"
                            canApprove={canBulkApprove}
                            approveLabel="Approve"
                            approveVerb="approved"
                        />
                    )}
                    {/* Iter60.p53 — Wrap in `asl-table-scroll` so the sticky
                        header + WebKit scrollbar policy from Payroll /
                        Attendance apply. `asl-table asl-table--zebra`
                        already delivers row separators + hover + zebra. */}
                    <div className="asl-table-scroll">
                    <table className="asl-table asl-table--zebra" data-testid="leave-table">
                        <thead>
                            <tr>
                            {(canBulkApprove || canBulkDelete) && (
                                <th scope="col" className="px-3 py-3 w-8">
                                    <Checkbox
                                        checked={bulk.allVisibleSelected}
                                        onCheckedChange={bulk.toggleAll}
                                        data-testid="bulk-select-all"
                                    />
                                </th>
                            )}
                            <th scope="col" className="px-4 py-3 text-left">Module ID</th>
                            <th scope="col" className="px-4 py-3 text-left">Employee</th>
                            <th scope="col" className="px-4 py-3 text-left">Type</th>
                            <th scope="col" className="px-4 py-3 text-left">Kind</th>
                            <th scope="col" className="px-4 py-3 text-left">Dates</th>
                            <th scope="col" className="px-4 py-3 text-right">Days</th>
                            <th scope="col" className="px-4 py-3 text-left">Status</th>
                            <th scope="col" className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((r) => (
                                <tr key={r.id} {...rowOpenProps(navigate, () => setDetail(r), { testid: `leave-row-${r.id}`, className: 'border-t' })} style={{ borderColor: "var(--asl-border)" }}>
                                    {(canBulkApprove || canBulkDelete) && (
                                        <td className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                checked={bulk.isSelected(r.id)}
                                                onCheckedChange={() => bulk.toggle(r.id)}
                                                data-testid={`bulk-select-${r.id}`}
                                            />
                                        </td>
                                    )}
                                    {/* Iter60.p47 Item D — Module ID column. Prefers the
                                        backend-hydrated `module_employee_id`, falls back to
                                        the resolved employees map. */}
                                    <td className="px-4 py-3" data-testid={`leave-module-id-${r.id}`}>
                                        <ModuleIdChip
                                            value={r.module_employee_id || empMap[r.employee_id]?.module_employee_id}
                                            testid={`leave-module-id-chip-${r.id}`}
                                        />
                                    </td>
                                    <td className="px-4 py-3">
                                        {r.employee_id ? (
                                            <EntityLink to={`/m/${pathModuleCode}/staff/${r.employee_id}`} testid={`leave-emp-link-${r.id}`}>
                                                {r.employee_name || r.employee_display || empMap[r.employee_id]?.full_name || "—"}
                                            </EntityLink>
                                        ) : (r.employee_name || r.employee_display || "—")}
                                    </td>
                                    <td className="px-4 py-3">{typeMap[r.leave_type_id]?.name || "—"}</td>
                                    <td className="px-4 py-3">{r.request_kind}</td>
                                    <td className="px-4 py-3 mono text-xs">{r.start_date} → {r.end_date}</td>
                                    <td className="px-4 py-3 text-right tabular-nums font-semibold asl-navy-text" data-no-row-click>{r.total_days}</td>
                                    <td className="px-4 py-3">
                                        <span className={`text-[10px] uppercase font-bold tracking-wider rounded-full px-2 py-0.5 ${STATUS_TONE[r.status] || "bg-gray-100"}`}
                                              aria-label={`Status: ${r.status}`}>{r.status}</span>
                                    </td>
                                    <td className="px-4 py-3 text-right space-x-1" data-no-row-click>
                                        {r.status === "pending_dept" && canDeptApprove && (
                                            <Button size="sm" variant="outline" onClick={() => action(r.id, "dept-approve")} data-testid={`leave-dept-${r.id}`}>Dept Approve</Button>
                                        )}
                                        {r.status === "pending_hr" && canHRApprove && (
                                            <Button size="sm" className="asl-navy-bg text-white" onClick={() => action(r.id, "hr-approve")} data-testid={`leave-hr-${r.id}`}>HR Approve</Button>
                                        )}
                                        {["pending_dept", "pending_hr"].includes(r.status) && (
                                            <Button size="sm" variant="outline" onClick={() => action(r.id, "reject")} data-testid={`leave-reject-${r.id}`}>Reject</Button>
                                        )}
                                        {r.status !== "cancelled" && r.status !== "rejected" && !r.deleted_at && (
                                            <Button size="sm" variant="ghost" onClick={() => action(r.id, "cancel")} data-testid={`leave-cancel-${r.id}`}>Cancel</Button>
                                        )}
                                        {r.deleted_at && <DeletedPill deletedAt={r.deleted_at} />}
                                        <RowActions
                                            entity="leave"
                                            endpoint="/leave/requests"
                                            id={r.id}
                                            label={r.id.slice(0, 8)}
                                            deleted={!!r.deleted_at}
                                            moduleId={r.module_id}
                                            hardDeleteSupported={false}
                                            onChange={load}
                                            testid={`leave-actions-${r.id}`}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                    </>
                )}
            </div>

            <NewLeaveDialog open={open} onOpenChange={setOpen} onSaved={load}
                types={types} employees={employees} moduleId={scopedId} />
                </TabsContent>
                <TabsContent value="calendar" className="pt-3">
                    <LeaveCalendarTab moduleId={scopedId} types={types} employees={employees} />
                </TabsContent>
                <TabsContent value="balances" className="pt-3">
                    <LeaveBalancesTabWrapper moduleId={scopedId} types={types} />
                </TabsContent>
            </Tabs>
            <DetailDialog
                row={detail ? (() => { const { attachments, ...rest } = detail; return rest; })() : null}
                onClose={() => setDetail(null)}
                title="Leave Request"
                testid="detail-detail"
                moduleCodeFallback={pathModuleCode}
                actions={detail ? [
                    { label: "Dept Approve", visible: detail.status === "pending_dept" && canDeptApprove, onClick: async () => { await action(detail.id, "dept-approve"); setDetail(null); }, testid: "leave-dept-approve", className: "asl-navy-bg text-white" },
                    { label: "HR Approve", visible: detail.status === "pending_hr" && canHRApprove, onClick: async () => { await action(detail.id, "hr-approve"); setDetail(null); }, testid: "leave-hr-approve", className: "asl-navy-bg text-white" },
                    { label: "Reject", visible: detail.status?.startsWith("pending") && (canDeptApprove || canHRApprove), onClick: async () => { await action(detail.id, "reject"); setDetail(null); }, testid: "leave-reject" },
                    { label: "Cancel", visible: detail.status?.startsWith("pending"), onClick: async () => { await action(detail.id, "cancel"); setDetail(null); }, testid: "leave-cancel" },
                ] : []}
            >
                {detail && (
                    <div className="pt-3" data-testid="detail-detail-attachments">
                        <AttachmentsField
                            requestId={detail.id}
                            liveAttachments={detail.attachments || []}
                            onLiveChange={(next) => setDetail({ ...detail, attachments: next })}
                            label="Attachments"
                            testid="leave-detail-attachments"
                            canDelete={(att) => {
                                // Admin can always delete; reporter can
                                // delete BEFORE approval only.
                                const isAdmin = user?.role === "kaafi_system_admin";
                                const isReporter = detail.created_by === user?.id;
                                const locked = ["approved", "rejected", "cancelled"].includes(detail.status);
                                return isAdmin || (isReporter && !locked);
                            }}
                            disabled={!["kaafi_system_admin", "kaafi_hr_manager", "asl_department_manager"].includes(user?.role)}
                        />
                    </div>
                )}
            </DetailDialog>
            {/* Iter73 — Balance-overrun approve confirmation. */}
            <Dialog open={!!overrunDialog} onOpenChange={(o) => { if (!o) setOverrunDialog(null); }}>
                <DialogContent data-testid="leave-overrun-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[color:var(--asl-crimson)]">
                            ⚠ Balance overrun
                        </DialogTitle>
                    </DialogHeader>
                    {overrunDialog && (
                        <div className="space-y-4 text-sm">
                            <div
                                className="rounded p-3"
                                style={{
                                    background: "rgba(168,50,74,0.08)",
                                    border: "1px solid rgba(168,50,74,0.2)",
                                }}
                            >
                                <div><strong>{overrunDialog.employee_name}</strong> has{" "}
                                    <strong>{overrunDialog.available_before_approval}</strong>{" "}
                                    {overrunDialog.leave_type_name} days available but the request
                                    consumes <strong>{overrunDialog.requested_days}</strong> days.</div>
                                <div className="mt-1">
                                    Approving will send their balance to{" "}
                                    <strong data-testid="leave-overrun-negative-balance">
                                        −{overrunDialog.overrun_days}
                                    </strong>{" "}
                                    days (negative).
                                </div>
                                <div className="mt-1 opacity-80">
                                    Future monthly accruals will absorb this deficit until the balance
                                    returns to zero.
                                </div>
                            </div>
                            <label className="flex items-start gap-2 cursor-pointer">
                                <Checkbox
                                    checked={overrunAck}
                                    onCheckedChange={(v) => setOverrunAck(!!v)}
                                    data-testid="leave-overrun-ack-checkbox"
                                />
                                <span>I acknowledge and approve the balance overrun.</span>
                            </label>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setOverrunDialog(null)}
                            data-testid="leave-overrun-cancel"
                        >Cancel</Button>
                        <Button
                            type="button"
                            className="asl-navy-bg text-white"
                            disabled={!overrunAck}
                            data-testid="leave-overrun-confirm"
                            onClick={async () => {
                                const d = overrunDialog;
                                setOverrunDialog(null);
                                setOverrunAck(false);
                                await action(d.id, d.path, { acknowledged_overrun: true });
                            }}
                        >Approve with overrun</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}

function LeaveCalendarTab({ moduleId, types, employees }) {
    const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const empMap = useMemo(() => Object.fromEntries(employees.map(e => [e.id, e])), [employees]);
    const typeMap = useMemo(() => Object.fromEntries(types.map(t => [t.id, t])), [types]);
    useEffect(() => {
        setLoading(true);
        const params = { month };
        if (moduleId) params.module_id = moduleId;
        api.get("/leave/calendar", { params })
            .then(({ data }) => setItems(data.items || []))
            .catch(err => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [month, moduleId]);
    const [y, m] = month.split("-").map(Number);
    const firstDay = new Date(y, m - 1, 1);
    const daysInMonth = new Date(y, m, 0).getDate();
    const startWeekday = firstDay.getDay();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    const dayItems = (d) => {
        const iso = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        return items.filter(it => it.start_date <= iso && it.end_date >= iso);
    };
    const prev = () => { const dd = new Date(y, m - 2, 1); setMonth(dd.toISOString().slice(0, 7)); };
    const next = () => { const dd = new Date(y, m, 1); setMonth(dd.toISOString().slice(0, 7)); };
    return (
        <div className="asl-card p-5 space-y-3" data-testid="leave-calendar">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={prev} data-testid="leave-cal-prev">‹ Prev</Button>
                    <Button size="sm" variant="outline" onClick={next} data-testid="leave-cal-next">Next ›</Button>
                </div>
                <div className="flex items-center gap-3">
                    <span className="asl-crimson-text font-bold" aria-hidden="true">/</span>
                    <div className="text-base font-bold asl-navy-text tracking-tight" data-testid="leave-cal-label">
                        {firstDay.toLocaleString("en-US", { month: "long", year: "numeric" })}
                    </div>
                </div>
                <div className="text-[11px] text-[color:var(--asl-muted)] mono uppercase tracking-wider">
                    {loading ? "loading…" : `${items.length} approved`}
                </div>
            </div>
            <div className="grid grid-cols-7 gap-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--asl-muted)] border-b pb-2"
                 style={{ borderColor: "var(--asl-border)" }}>
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => <div key={d} className="text-center p-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {cells.map((d, i) => {
                    const dayList = d ? dayItems(d) : [];
                    const hasItems = dayList.length > 0;
                    return (
                        <div key={i}
                             className="min-h-[80px] rounded-md border p-1.5 text-[10px] transition-colors hover:bg-[color:var(--asl-navy-50,#eef1f8)]"
                             style={{
                                 borderColor: "var(--asl-border)",
                                 background: d ? "#fff" : "rgba(0,0,0,0.02)",
                             }}>
                            {d && (
                                <div className={`font-bold ${hasItems ? "asl-crimson-text" : "asl-navy-text"}`}>
                                    {d}
                                </div>
                            )}
                            {d && dayList.slice(0, 3).map(it => (
                                <div key={it.id}
                                     className="px-1.5 py-0.5 rounded mt-1 truncate font-semibold"
                                     style={{
                                         background: "rgba(168, 50, 74, 0.10)",
                                         color: "var(--asl-crimson)",
                                         border: "1px solid rgba(168, 50, 74, 0.18)",
                                     }}
                                     title={`${empMap[it.employee_id]?.full_name || ""} — ${typeMap[it.leave_type_id]?.name || ""}`}
                                     data-testid={`leave-cal-chip-${it.id}`}>
                                    {empMap[it.employee_id]?.full_name?.split(" ")[0] || "?"}
                                </div>
                            ))}
                            {d && dayList.length > 3 && (
                                <div className="text-[9px] text-[color:var(--asl-muted)] mt-1 font-medium">
                                    +{dayList.length - 3} more
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function LeaveBalancesTabWrapper({ moduleId, types }) {
    // Iter60.p47 Item C — Wrapper toggles between the new search-first
    // lookup (default) and the legacy flat list.
    const [showFlat, setShowFlat] = useState(false);
    const [year, setYear] = useState(new Date().getFullYear());
    return (
        <div className="space-y-3" data-testid="leave-balances">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex gap-2 items-center">
                    <span className="text-xs font-semibold uppercase tracking-wider text-[color:var(--asl-muted)]">Year</span>
                    <select
                        value={year}
                        onChange={(e) => setYear(parseInt(e.target.value, 10))}
                        className="h-9 px-3 rounded-md border text-sm font-semibold asl-navy-text bg-white transition-shadow hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--asl-crimson)] focus:ring-offset-1"
                        data-testid="bal-year-select"
                        style={{ borderColor: "var(--asl-border)" }}
                        aria-label="Select year for leave balances"
                    >
                        {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                <label className="inline-flex items-center gap-2 text-xs text-[color:var(--asl-muted)] cursor-pointer select-none hover:asl-navy-text transition-colors">
                    <input
                        type="checkbox"
                        checked={showFlat}
                        onChange={(e) => setShowFlat(e.target.checked)}
                        data-testid="bal-show-flat-toggle"
                        className="accent-[color:var(--asl-crimson)]"
                    />
                    Show classic flat list
                </label>
            </div>
            {showFlat
                ? <LeaveBalancesTab moduleId={moduleId} types={types} year={year} />
                : <LeaveBalanceLookup moduleId={moduleId} year={year} />}
        </div>
    );
}

function LeaveBalancesTab({ moduleId, types, year: yearProp }) {
    const { moduleCode: pathModuleCode } = useParams();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    // Iter60.p47 Item D — search chip on the classic flat list (debounced).
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);
    const year = yearProp;
    useEffect(() => {
        setLoading(true);
        const params = { year };
        if (moduleId) params.module_id = moduleId;
        if (debouncedSearch) params.search = debouncedSearch;
        api.get("/leave/balances", { params })
            .then(({ data }) => setRows(data || []))
            .catch(err => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [year, moduleId, debouncedSearch]);

    // Iter60.p49 Item E — pivot rows (one row per employee×leave_type)
    // into a matrix keyed by employee, keeping the 5 leave-type columns
    // in the mandated order: annual → sick → unpaid → compassionate →
    // permission_hourly. Each cell is `{entitled, used}`. Missing types
    // for an employee render as 0/0 (backend `_ensure_balance` creates
    // them lazily, but a race between first-read and pivot could still
    // yield a partial set — the fallback prevents NaNs in the UI).
    const LEAVE_TYPE_ORDER = [
        "annual", "sick", "marriage", "hajj_umrah", "maternity",
        "paternity", "breastfeeding", "personal", "compassionate",
        "permission_hourly", "unpaid",
    ];
    const LEAVE_TYPE_LABEL = {
        annual: "Annual",
        sick: "Sick",
        marriage: "Marriage",
        hajj_umrah: "Hajj/Umrah",
        maternity: "Maternity",
        paternity: "Paternity",
        breastfeeding: "Breastfeeding",
        personal: "Personal",
        compassionate: "Compassionate",
        permission_hourly: "Permission (hourly)",
        unpaid: "Unpaid",
    };
    const pivoted = React.useMemo(() => {
        const byEmp = new Map();
        for (const r of rows) {
            let bucket = byEmp.get(r.employee_id);
            if (!bucket) {
                bucket = {
                    employee_id: r.employee_id,
                    employee_name: r.employee_name,
                    employee_code: r.employee_code,
                    module_employee_id: r.module_employee_id,
                    module_id: r.module_id,
                    balances: {},
                    // Iter60.p63 — cycle metadata carried on the row
                    // for the Annual accrual tooltip.
                    annual_accrual: null,
                    // Iter60.p64 — sick cumulative + tier for tooltip.
                    sick_snapshot: null,
                };
                byEmp.set(r.employee_id, bucket);
            }
            bucket.balances[r.leave_type_code] = {
                entitled: r.entitled_days,
                // Iter60.p68 — raw config value; null → "no fixed cap"
                entitled_config: r.entitled_days_per_year,
                accrued: r.accrued_days,
                used: r.used_days,
            };
            if (r.leave_type_code === "annual" && r.accrual) {
                bucket.annual_accrual = r.accrual;
            }
            if (r.leave_type_code === "sick" && r.sick) {
                bucket.sick_snapshot = r.sick;
            }
        }
        return Array.from(byEmp.values()).sort((a, b) =>
            String(a.employee_name || "").localeCompare(String(b.employee_name || "")),
        );
    }, [rows]);

    const csv = () => {
        // Iter60.p49 Items B + E — CSV column layout mirrors the on-
        // screen pivot exactly. EMPLOYEE_CODE column REMOVED (Item B);
        // 5×(Entitled + Used) columns replace the previous single
        // Entitled/Used/Pending/Available quartet.
        const header = [
            "Module ID", "Employee",
            ...LEAVE_TYPE_ORDER.flatMap((c) => [
                `Entitled (${LEAVE_TYPE_LABEL[c]})`,
                `Used (${LEAVE_TYPE_LABEL[c]})`,
            ]),
        ].join(",");
        const escape = (v) => {
            const s = String(v ?? "");
            if (s.includes(",") || s.includes('"') || s.includes("\n")) {
                return `"${s.replace(/"/g, '""')}"`;
            }
            return s;
        };
        const lines = pivoted.map((p) => {
            const cells = [p.module_employee_id || "", p.employee_name || ""];
            for (const code of LEAVE_TYPE_ORDER) {
                const b = p.balances[code] || { entitled: 0, used: 0 };
                cells.push(b.entitled, b.used);
            }
            return cells.map(escape).join(",");
        });
        const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `leave-balances-${year}.csv`; a.click();
    };
    return (
        <div className="space-y-3" data-testid="leave-balances-flat">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="relative">
                    <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--asl-muted)]" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search name or Module ID…"
                        className="h-9 pl-8 pr-8 w-72 rounded border text-sm bg-transparent"
                        style={{ borderColor: "var(--asl-border)" }}
                        data-testid="bal-search-input"
                    />
                    {search && (
                        <button
                            type="button"
                            onClick={() => setSearch("")}
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100"
                            aria-label="Clear"
                            data-testid="bal-search-clear"
                        >
                            <X className="h-3.5 w-3.5 text-[color:var(--asl-muted)]" />
                        </button>
                    )}
                </div>
                <Button variant="outline" size="sm" onClick={csv} data-testid="bal-csv-btn">Download CSV</Button>
            </div>
            <div className="asl-card overflow-hidden">
                {loading ? <TableSkeleton rows={5} cols={12} /> : pivoted.length === 0 ? (
                    <EmptyState icon={Calendar} title="No balances yet" description="Balances are auto-created on first read for active employees." testid="bal-empty" />
                ) : (
                    <div className="asl-table-scroll">
                    <table className="asl-table asl-table--zebra" data-testid="bal-table">
                        <thead>
                            <tr>
                                <th scope="col" className="px-4 py-3 text-left sticky left-0 z-20 text-[11px] uppercase tracking-wider font-bold asl-navy-text" style={{ background: "var(--asl-bg-alt)" }}>Module ID</th>
                                <th scope="col" className="px-4 py-3 text-left sticky z-20 text-[11px] uppercase tracking-wider font-bold asl-navy-text" style={{ left: "88px", background: "var(--asl-bg-alt)" }}>Employee</th>
                                {LEAVE_TYPE_ORDER.map((code) => (
                                    <React.Fragment key={code}>
                                        <th scope="col" className="px-3 py-3 text-right whitespace-nowrap" title={`${LEAVE_TYPE_LABEL[code]} — entitled days`}>
                                            <div className="text-[10px] uppercase tracking-wider font-medium text-[color:var(--asl-muted)] leading-tight">Entitled</div>
                                            <div className="text-[13px] font-bold asl-navy-text leading-tight mt-0.5" data-testid={`bal-th-entitled-${code}`}>{LEAVE_TYPE_LABEL[code]}</div>
                                        </th>
                                        <th scope="col" className="px-3 py-3 text-right whitespace-nowrap" title={`${LEAVE_TYPE_LABEL[code]} — used days`}>
                                            <div className="text-[10px] uppercase tracking-wider font-medium text-[color:var(--asl-muted)] leading-tight">Used</div>
                                            <div className="text-[13px] font-bold asl-navy-text leading-tight mt-0.5" data-testid={`bal-th-used-${code}`}>{LEAVE_TYPE_LABEL[code]}</div>
                                        </th>
                                    </React.Fragment>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pivoted.map((p) => (
                                <tr key={p.employee_id} style={{ borderColor: "var(--asl-border)" }} data-testid={`bal-emp-row-${p.employee_id}`}>
                                    <td className="px-4 py-3 sticky left-0 bg-white" data-testid={`bal-module-id-${p.employee_id}`}>
                                        <ModuleIdChip value={p.module_employee_id} testid={`bal-module-id-chip-${p.employee_id}`} />
                                    </td>
                                    <td className="px-4 py-3 sticky bg-white font-medium asl-navy-text" style={{ left: "88px" }}>
                                        {p.employee_id ? (
                                            <EntityLink to={`/m/${pathModuleCode}/staff/${p.employee_id}`} testid={`leave-balance-emp-${p.employee_id}`}>
                                                {p.employee_name}
                                            </EntityLink>
                                        ) : p.employee_name}
                                    </td>
                                    {LEAVE_TYPE_ORDER.map((code) => {
                                        const b = p.balances[code] || { entitled: 0, used: 0 };
                                        // Iter60.p63 → p68 — Annual entitled tooltip.
                                        // `b.entitled` is now the config cap (30);
                                        // `b.accrued` carries the p63 accrual snapshot.
                                        const annualTooltip = (code === "annual" && p.annual_accrual)
                                            ? (
                                                `Annual — Cycle ${p.annual_accrual.fiscal_year_start} → ${p.annual_accrual.fiscal_year_end}. `
                                                + `Next reset ${p.annual_accrual.next_reset}. `
                                                + `Accrued to date: ${p.annual_accrual.accrued} + carried over ${p.annual_accrual.carried_over} = ${b.accrued ?? 0}. `
                                                + `Configured cap: ${b.entitled}. `
                                                + `Policy: ${p.annual_accrual.carry_over_policy}`
                                                + (p.annual_accrual.carry_over_policy === "carry_over_capped"
                                                    ? ` (cap: ${p.annual_accrual.carry_over_cap_days} days).`
                                                    : ".")
                                            )
                                            : null;
                                        // Iter60.p64 — Sick used tooltip: current tier + cycle + next-tier hint.
                                        const sickTooltip = (code === "sick" && p.sick_snapshot)
                                            ? (
                                                `Sick — ${p.sick_snapshot.current_tier?.label || "Not started"}. `
                                                + `Cumulative in cycle: ${p.sick_snapshot.cumulative_used} days. `
                                                + `Cycle ${p.sick_snapshot.cycle_start} → ${p.sick_snapshot.cycle_end}.`
                                            )
                                            : null;
                                        return (
                                            <React.Fragment key={code}>
                                                <EntitledCell
                                                    value={b.entitled}
                                                    leaveTypeLabel={LEAVE_TYPE_LABEL[code]}
                                                    employeeName={p.employee_name}
                                                    testid={`bal-entitled-${code}-${p.employee_id}`}
                                                    tooltip={annualTooltip}
                                                />
                                                <UsedCell
                                                    value={b.used}
                                                    entitled={b.entitled}
                                                    leaveTypeLabel={LEAVE_TYPE_LABEL[code]}
                                                    employeeName={p.employee_name}
                                                    testid={`bal-used-${code}-${p.employee_id}`}
                                                    tooltip={sickTooltip}
                                                />
                                            </React.Fragment>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function NewLeaveDialog({ open, onOpenChange, onSaved, types, employees, moduleId }) {
    const today = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({
        employee_id: "", leave_type_id: "", request_kind: "full_day",
        start_date: today, end_date: today, start_time: "", end_time: "",
        reason: "", evidence_url: "", expected_delivery_date: "",
    });
    // Iter60.p67 — Universal staged-attachments list. File[] kept in
    // client state until the leave request is created; then uploaded
    // sequentially via POST /leave/requests/{id}/attachments.
    const [stagedFiles, setStagedFiles] = useState([]);
    useEffect(() => {
        if (open) {
            setForm({
                employee_id: "", leave_type_id: "", request_kind: "full_day",
                start_date: today, end_date: today, start_time: "09:00", end_time: "12:00",
                reason: "", evidence_url: "", expected_delivery_date: "",
            });
            setStagedFiles([]);
        }
        // eslint-disable-next-line
    }, [open]);

    // Iter60.p62 — Once the user picks an employee, resolve gender +
    // months of tenure so we can filter the leave-type dropdown and
    // show inline eligibility hints.
    const selectedEmp = useMemo(
        () => employees.find((e) => e.id === form.employee_id) || null,
        [employees, form.employee_id],
    );
    const monthsOfTenure = useMemo(() => {
        if (!selectedEmp?.hire_date) return null;
        try {
            const hd = new Date(selectedEmp.hire_date);
            const now = new Date();
            let m = (now.getFullYear() - hd.getFullYear()) * 12 + (now.getMonth() - hd.getMonth());
            if (now.getDate() < hd.getDate()) m -= 1;
            return Math.max(m, 0);
        } catch {
            return null;
        }
    }, [selectedEmp]);

    function eligibility(t) {
        if (t.active === false || t.is_active === false) return { eligible: false, reason: "Inactive" };
        if (!selectedEmp) return { eligible: true, reason: null };
        const gender = (selectedEmp.gender || "").toLowerCase();
        if (t.gender_restriction && gender !== t.gender_restriction) {
            if (!gender) return { eligible: false, reason: "Employee gender not set" };
            return { eligible: false, reason: `${t.gender_restriction} only` };
        }
        const gate = Number(t.tenure_gate_months || 0);
        if (gate > 0) {
            if (monthsOfTenure === null) return { eligible: false, reason: "hire_date missing" };
            if (monthsOfTenure < gate) return { eligible: false, reason: `${gate}mo tenure required (has ${monthsOfTenure})` };
        }
        return { eligible: true, reason: null };
    }

    const displayTypes = useMemo(() => (
        (types || []).filter((t) => t.active !== false && t.is_active !== false)
    ), [types]);

    const selectedType = useMemo(
        () => displayTypes.find((t) => t.id === form.leave_type_id) || null,
        [displayTypes, form.leave_type_id],
    );

    // Force hourly kind whenever an hourly type is picked.
    useEffect(() => {
        if (!selectedType) return;
        if (selectedType.is_hourly && form.request_kind !== "permission_hours") {
            setForm((f) => ({ ...f, request_kind: "permission_hours" }));
        }
    }, [selectedType]); // eslint-disable-line react-hooks/exhaustive-deps

    // Live total days for min/max hint.
    const totalDaysPreview = useMemo(() => {
        if (form.request_kind === "permission_hours") return null;
        try {
            const d1 = new Date(form.start_date);
            const d2 = new Date(form.end_date);
            const days = Math.round((d2 - d1) / (24 * 3600 * 1000)) + 1;
            return days > 0 ? days : null;
        } catch { return null; }
    }, [form.start_date, form.end_date, form.request_kind]);

    const minMaxError = useMemo(() => {
        if (!selectedType || totalDaysPreview === null) return null;
        const min = selectedType.min_days;
        const max = selectedType.max_days;
        if (min !== null && min !== undefined && totalDaysPreview < Number(min)) {
            return `${selectedType.name} requires a minimum of ${min} day(s).`;
        }
        if (max !== null && max !== undefined && totalDaysPreview > Number(max)) {
            return `${selectedType.name} allows a maximum of ${max} day(s).`;
        }
        return null;
    }, [selectedType, totalDaysPreview]);

    const evidenceLabel = selectedType?.evidence_required || null;
    const articleRef = selectedType?.article_reference || null;

    // Iter60.p64 — Sick Leave tier preview. When the picked type is
    // Sick and both employee + dates are set, fetch the cumulative-in-
    // cycle and current tier so the UI can render "N cumulative → Tier
    // X" inline. Debounced on employee/type/days change.
    const [sickPreview, setSickPreview] = useState(null);
    useEffect(() => {
        if (!selectedEmp?.id || selectedType?.code !== "sick" || !totalDaysPreview) {
            setSickPreview(null);
            return;
        }
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get(
                    `/employees/${selectedEmp.id}/sick-leave-tier-preview`,
                    { params: { additional_days: totalDaysPreview, on_date: form.start_date } },
                );
                if (!cancelled) setSickPreview(data);
            } catch {
                if (!cancelled) setSickPreview(null);
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [selectedEmp?.id, selectedType?.code, totalDaysPreview, form.start_date]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.employee_id) { toast.error("Pick an employee first."); return; }
        if (!form.leave_type_id) { toast.error("Pick a leave type."); return; }
        const elig = eligibility(selectedType || {});
        if (!elig.eligible) { toast.error(elig.reason || "Not eligible."); return; }
        if (minMaxError) { toast.error(minMaxError); return; }
        // Iter60.p67 — Required-evidence types must have at least one
        // staged file (or a legacy evidence_url from EvidenceUpload).
        const requiresEvidence = !!evidenceLabel;
        if (requiresEvidence && stagedFiles.length === 0 && !form.evidence_url) {
            toast.error(`${evidenceLabel} is required. Add at least one attachment.`);
            return;
        }
        try {
            const payload = {
                ...form,
                has_pending_attachments: stagedFiles.length > 0,
            };
            const { data: created } = await api.post("/leave/requests", payload);
            // Upload staged files sequentially. If any file fails,
            // surface the error but keep the rest — the leave request
            // itself already exists.
            let uploaded = 0;
            let failed = 0;
            for (const f of stagedFiles) {
                try {
                    const fd = new FormData();
                    fd.append("file", f);
                    await api.post(
                        `/leave/requests/${created.id}/attachments`,
                        fd,
                        { headers: { "Content-Type": "multipart/form-data" } },
                    );
                    uploaded += 1;
                } catch {
                    failed += 1;
                }
            }
            if (failed > 0) {
                toast.error(
                    `Leave request submitted, but ${failed} attachment(s) failed to upload. Open the request to retry.`,
                );
            } else if (uploaded > 0) {
                toast.success(`Request submitted with ${uploaded} attachment(s)`);
            } else {
                toast.success("Request submitted");
            }
            onOpenChange(false);
            await onSaved();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="leave-new-dialog">
                <DialogHeader><DialogTitle>New Leave Request</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3 pt-1">
                    <div>
                        <Label className="text-xs uppercase">Employee</Label>
                        <EmployeePicker
                            moduleId={moduleId}
                            employees={employees}
                            value={form.employee_id}
                            onChange={(id) => setForm({ ...form, employee_id: id, leave_type_id: "" })}
                            placeholder="Pick employee"
                            testid="leave-emp-picker"
                            filter={(e) => e && e.employment_type !== "casual"}
                        />
                        {selectedEmp && (
                            <div className="text-[11px] asl-text-3 mt-1" data-testid="leave-emp-meta">
                                Gender: <span className="font-semibold">{selectedEmp.gender || "not set"}</span>
                                {" · "}
                                Tenure: <span className="font-semibold">{monthsOfTenure ?? "unknown"} months</span>
                                {!selectedEmp.gender && (
                                    <span className="ml-2 asl-crimson-text">
                                        (gender-restricted types blocked until this is set)
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase">Leave type</Label>
                            <Select value={form.leave_type_id} onValueChange={(v) => setForm({ ...form, leave_type_id: v })}>
                                <SelectTrigger data-testid="leave-type-select"><SelectValue placeholder="Pick leave type" /></SelectTrigger>
                                <SelectContent>
                                    {displayTypes.map((t) => {
                                        const elig = eligibility(t);
                                        return (
                                            <SelectItem
                                                key={t.id}
                                                value={t.id}
                                                disabled={!elig.eligible}
                                                data-testid={`leave-type-option-${t.code}`}
                                            >
                                                <span className={elig.eligible ? "" : "opacity-60"}>
                                                    {t.name}
                                                    {!elig.eligible && (
                                                        <span className="text-[10px] ml-2 asl-crimson-text">
                                                            · not eligible ({elig.reason})
                                                        </span>
                                                    )}
                                                </span>
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                            {articleRef && (
                                <div
                                    className="text-[11px] asl-text-3 mt-1 mono"
                                    data-testid="leave-type-article-ref"
                                >
                                    {articleRef}
                                </div>
                            )}
                        </div>
                        <div>
                            <Label className="text-xs uppercase">Kind</Label>
                            <Select
                                value={form.request_kind}
                                onValueChange={(v) => setForm({ ...form, request_kind: v })}
                                disabled={!!selectedType?.is_hourly}
                            >
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="full_day">Full day</SelectItem>
                                    <SelectItem value="permission_hours">Hourly</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs uppercase">Start date</Label><DateInput value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} /></div>
                        <div><Label className="text-xs uppercase">End date</Label><DateInput value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} /></div>
                    </div>
                    {form.request_kind === "permission_hours" && (
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs uppercase">Start time</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
                            <div><Label className="text-xs uppercase">End time</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
                        </div>
                    )}
                    {selectedType?.code === "maternity" && (
                        <div className="space-y-2" data-testid="leave-maternity-block">
                            <div>
                                <Label className="text-xs uppercase">Expected delivery date</Label>
                                <DateInput
                                    value={form.expected_delivery_date || ""}
                                    onChange={(v) => setForm({ ...form, expected_delivery_date: v })}
                                    data-testid="leave-expected-delivery-date"
                                />
                                {form.expected_delivery_date && (() => {
                                    // Iter60.p65 — Enforce the 6-week (42-day) pre-delivery
                                    // rule inline so HR sees the block before submitting.
                                    const dd = new Date(form.expected_delivery_date);
                                    const earliest = new Date(dd);
                                    earliest.setDate(earliest.getDate() - 42);
                                    const startD = form.start_date ? new Date(form.start_date) : null;
                                    const earliestIso = earliest.toISOString().slice(0, 10);
                                    const violates = startD && startD > earliest;
                                    return (
                                        <p
                                            className={"text-[11px] mt-1 " + (violates ? "asl-crimson-text font-semibold" : "text-[color:var(--asl-muted)]")}
                                            data-testid="leave-maternity-delivery-hint"
                                        >
                                            Earliest maternity start: <span className="mono">{earliestIso}</span>{" "}
                                            (6 weeks / 42 days before delivery per Article 129).
                                            {violates && (
                                                <> The chosen start <span className="mono">{form.start_date}</span> is too late.</>
                                            )}
                                        </p>
                                    );
                                })()}
                            </div>
                            <div
                                className="text-xs px-3 py-2 rounded"
                                style={{
                                    background: "rgba(228, 155, 45, 0.12)",
                                    color: "#8f5b0a",
                                    border: "1px solid rgba(228, 155, 45, 0.25)",
                                }}
                                data-testid="leave-maternity-note"
                            >
                                <strong>Article 129:</strong> 14 weeks (98 days) full pay maternity leave.
                                Minimum 6 weeks must fall before the expected delivery date.
                                Attach the medical certificate confirming the delivery date under Evidence below.
                            </div>
                        </div>
                    )}
                    {selectedType?.code === "paternity" && (
                        <div
                            className="text-xs px-3 py-2 rounded"
                            style={{
                                background: "rgba(27, 42, 86, 0.08)",
                                color: "var(--asl-navy)",
                                border: "1px solid rgba(27, 42, 86, 0.18)",
                            }}
                            data-testid="leave-paternity-note"
                        >
                            <strong>Article 129(6):</strong> 2 weeks (14 days) full pay paternity leave.
                            Attach the birth certificate under Evidence below.
                        </div>
                    )}
                    {selectedType?.code === "sick" && sickPreview && (
                        <div
                            className="text-xs px-3 py-2 rounded space-y-1 asl-navy-text"
                            style={{
                                background: "rgba(27,42,86,0.06)",
                                border: "1px solid rgba(27,42,86,0.15)",
                            }}
                            data-testid="leave-sick-tier-preview"
                        >
                            {/* Iter73 — Probation-blocked banner removed.
                                Employees on probation can now request any
                                leave type; only leave-type-level
                                `tenure_gate_months` still gates (Maternity
                                6 mo, etc. — enforced server-side). */}
                            <div>
                                <strong>Sick tier preview:</strong>{" "}
                                <span data-testid="leave-sick-tier-label">{sickPreview.tier?.label || "—"}</span>
                            </div>
                            <div className="opacity-80">
                                Cumulative in cycle: {sickPreview.cumulative_used} used +{" "}
                                {sickPreview.additional_days} new = {sickPreview.cumulative_after} days.
                                Cycle <span className="mono">{sickPreview.cycle_start}</span> →{" "}
                                <span className="mono">{sickPreview.cycle_end}</span>.
                            </div>
                            {sickPreview.tier && sickPreview.daily_rate > 0 && (
                                <div className="opacity-80">
                                    Estimated pay: {sickPreview.additional_days} ×{" "}
                                    ${sickPreview.daily_rate.toFixed(2)} ×{" "}
                                    {sickPreview.tier.percentage}% ={" "}
                                    <strong data-testid="leave-sick-tier-amount">
                                        ${(sickPreview.additional_days * sickPreview.daily_rate * sickPreview.tier.percentage / 100).toFixed(2)}
                                    </strong>
                                </div>
                            )}
                        </div>
                    )}
                    {minMaxError && (
                        <div
                            className="text-xs asl-crimson-text"
                            data-testid="leave-minmax-error"
                        >
                            {minMaxError}
                        </div>
                    )}
                    {/* Iter60.p67 — Universal attachments field. Required
                        for evidence-required leave types (Sick, Marriage,
                        Maternity, etc.); optional for Annual, Unpaid,
                        Personal, Compassionate, Permission-hours. */}
                    <AttachmentsField
                        value={stagedFiles}
                        onChange={setStagedFiles}
                        required={!!evidenceLabel}
                        label={
                            evidenceLabel
                                ? `${evidenceLabel} (required)`
                                : "Attachments (optional)"
                        }
                        testid="leave-attachments"
                    />
                    <div><Label className="text-xs uppercase">Reason</Label><Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button
                            type="submit"
                            className="asl-navy-bg text-white"
                            data-testid="leave-submit"
                            disabled={!!minMaxError}
                        >Submit leave request</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
